/** @format */

import axios from 'axios';
import { _handleError } from '../helpers/api.helpers';
import { getCurrentTenantId } from '../helpers';

export const BASE_URL = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_PREFIX}`;
export const V2_BASE_URL = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_PREFIX_V2}`;

const TENANT_ID_EXEMPT_ENDPOINTS = ['/tenant/slug', '/tenant/domain'];

const isEndpointExemptFromTenantId = (url: string): boolean => {
  return TENANT_ID_EXEMPT_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const baseInstance = () =>
  axios.create({
    baseURL: BASE_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': true,
    },
  });

const v2BaseInstance = () =>
  axios.create({
    baseURL: V2_BASE_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': true,
    },
  });

const formDataInstance = axios.create({
  baseURL: BASE_URL,
  timeoutErrorMessage: 'Network Timeout',
  headers: {
    Accept: '*/*',
    'Content-Type': 'multipart/form-data; ',
    'ngrok-skip-browser-warning': true,
  },
});

const instance = baseInstance();
const refreshInstance = baseInstance();
const v2Instance = v2BaseInstance();

//request interceptors
refreshInstance.interceptors.request.use((config: any) => {
  const isExempt = config.url
    ? isEndpointExemptFromTenantId(config.url)
    : false;

  if (!isExempt) {
    const token = localStorage.getItem('refreshToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId && !isExempt) {
    console.warn(
      '[API] Request blocked: tenant ID not yet available for',
      config.url,
    );
    return Promise.reject(
      new Error(
        'Tenant ID is required but not available. Please ensure tenant is loaded.',
      ),
    );
  }

  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId;
  }

  return config;
});

instance.interceptors.request.use((config: any) => {
  const isExempt = config.url
    ? isEndpointExemptFromTenantId(config.url)
    : false;

  if (!isExempt) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId && !isExempt) {
    console.warn(
      '[API] Request blocked: tenant ID not yet available for',
      config.url,
    );
    return Promise.reject(
      new Error(
        'Tenant ID is required but not available. Please ensure tenant is loaded.',
      ),
    );
  }

  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId;
  }

  return config;
});

v2Instance.interceptors.request.use((config: any) => {
  const isExempt = config.url
    ? isEndpointExemptFromTenantId(config.url)
    : false;

  if (!isExempt) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId && !isExempt) {
    console.warn(
      '[API] Request blocked: tenant ID not yet available for',
      config.url,
    );
    return Promise.reject(
      new Error(
        'Tenant ID is required but not available. Please ensure tenant is loaded.',
      ),
    );
  }

  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId;
  }

  return config;
});

formDataInstance.interceptors.request.use((config: any) => {
  const isExempt = config.url
    ? isEndpointExemptFromTenantId(config.url)
    : false;

  if (!isExempt) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId && !isExempt) {
    console.warn(
      '[API] Request blocked: tenant ID not yet available for',
      config.url,
    );
    return Promise.reject(
      new Error(
        'Tenant ID is required but not available. Please ensure tenant is loaded.',
      ),
    );
  }

  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId;
  }

  return config;
});

let tries = 0;

//response interceptors
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    tries++;
    _handleError(error, tries);
    throw error;
  },
);

v2Instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    tries++;
    _handleError(error, tries);
    throw error;
  },
);

refreshInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    tries++;
    _handleError(error, tries, 'refresh');
    throw error;
  },
);

export { instance, refreshInstance, v2Instance, formDataInstance };
